import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import Header from "../../components/Header";
import { authFetchBlob, authGet, authPost, unwrapRows } from "../../lib/authApi";
import { ACTIVE_DEPARTMENT_EVENT, getActiveDepartment, setActiveDepartment } from "../../lib/departmentSelection";

function pickRows(resp) {
  if (Array.isArray(resp)) return resp;
  if (resp?.rows) return resp.rows;
  return unwrapRows(resp);
}

function getOrgId() {
  try {
    const user = JSON.parse(localStorage.getItem("api_user") || "{}");
    return user?.org_id || localStorage.getItem("org_id") || "";
  } catch {
    return localStorage.getItem("org_id") || "";
  }
}

function valueOf(row, keys) {
  for (const k of keys) {
    if (row?.[k]) return row[k];
  }
  return "";
}

export default function DepartmentResults() {
  const [orgId] = useState(getOrgId());
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(getActiveDepartment()?.id || "");
  const [analytes, setAnalytes] = useState([]);
  const [results, setResults] = useState([]);
  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const [form, setForm] = useState({
    patient_id: "",
    provider_id: "",
    facility_id: "",
    specimen_source: "",
    sample_type: "",
    collection_date: "",
    received_date: "",
  });
  const [positive, setPositive] = useState({});

  const selectedDepartment = useMemo(
    () => departments.find((d) => d.id === departmentId) || null,
    [departments, departmentId]
  );

  const loadDepartments = async () => {
    const resp = await authGet(`/departments/active?org_id=${encodeURIComponent(orgId)}`);
    const rows = pickRows(resp);
    setDepartments(rows);
    const current = getActiveDepartment();
    const stillExists = current?.id && rows.some((d) => d.id === current.id);
    if (stillExists) {
      setDepartmentId(current.id);
    } else if (!departmentId && rows.length) {
      setActiveDepartment(rows[0]);
      setDepartmentId(rows[0].id);
    }
  };

  const loadLists = async () => {
    const [p, pr, f] = await Promise.all([
      authGet(`/patients?org_id=${encodeURIComponent(orgId)}`).catch(() => ({ rows: [] })),
      authGet(`/providers?org_id=${encodeURIComponent(orgId)}`).catch(() => ({ rows: [] })),
      authGet(`/facilities?org_id=${encodeURIComponent(orgId)}`).catch(() => ({ rows: [] })),
    ]);
    setPatients(pickRows(p));
    setProviders(pickRows(pr));
    setFacilities(pickRows(f));
  };

  const loadDepartmentData = async (id = departmentId) => {
    if (!id) return;
    setBusy(true);
    try {
      const [a, r] = await Promise.all([
        authGet(`/departments/${id}/analytes?org_id=${encodeURIComponent(orgId)}`),
        authGet(`/departments/${id}/manual-results?org_id=${encodeURIComponent(orgId)}`),
      ]);
      const analyteRows = pickRows(a);
      setAnalytes(analyteRows);
      setPositive({});
      setResults(pickRows(r));
    } catch (e) {
      setToast({ open: true, msg: e.message || "Failed to load department", severity: "error" });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;
    loadDepartments().catch((e) => setToast({ open: true, msg: e.message, severity: "error" }));
    loadLists().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (departmentId) loadDepartmentData(departmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  useEffect(() => {
    const onActiveDepartmentChange = (event) => {
      const dept = event.detail || getActiveDepartment();
      setDepartmentId(dept?.id || "");
    };
    window.addEventListener(ACTIVE_DEPARTMENT_EVENT, onActiveDepartmentChange);
    return () => window.removeEventListener(ACTIVE_DEPARTMENT_EVENT, onActiveDepartmentChange);
  }, []);

  const submitManual = async () => {
    if (!departmentId) return;
    if (!form.patient_id) {
      setToast({ open: true, msg: "Choose a patient first.", severity: "warning" });
      return;
    }
    setCreating(true);
    try {
      const items = analytes.map((a) => ({ analyte_id: a.id, is_positive: !!positive[a.id] }));
      await authPost(`/departments/${departmentId}/manual-results`, { ...form, org_id: orgId, items, status: "final" });
      setToast({ open: true, msg: "Manual department report created.", severity: "success" });
      await loadDepartmentData(departmentId);
    } catch (e) {
      setToast({ open: true, msg: e.message || "Create failed", severity: "error" });
    } finally {
      setCreating(false);
    }
  };

  const openPdf = async (row) => {
    try {
      const blob = await authFetchBlob(`/departments/${departmentId}/manual-results/${row.id}/report?org_id=${encodeURIComponent(orgId)}`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setToast({ open: true, msg: e.message || "PDF failed", severity: "error" });
    }
  };

  const columns = [
    { field: "accession_number", headerName: "Accession", width: 180 },
    { field: "patient", headerName: "Patient", flex: 1, minWidth: 160 },
    { field: "provider", headerName: "Provider", flex: 1, minWidth: 140 },
    { field: "facility", headerName: "Facility", flex: 1, minWidth: 140 },
    { field: "status", headerName: "Status", width: 110, renderCell: (p) => <Chip size="small" label={p.value || "draft"} color={p.value === "final" ? "success" : "warning"} /> },
    { field: "report_date", headerName: "Report Date", width: 180, valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleString() : "—") },
    { field: "pdf", headerName: "PDF", width: 95, sortable: false, renderCell: (p) => <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => openPdf(p.row)}>Open</Button> },
  ];

  return (
    <Box m="20px">
      <Header title={`${selectedDepartment?.name || "Department"} Results`} subtitle="Use the sidebar department dropdown to switch lab departments." />
      {!orgId ? <Alert severity="warning">No organization found on your session. Log out and back in if departments do not load.</Alert> : null}
      {!selectedDepartment ? <Alert severity="info" sx={{ mb: 2 }}>No department is selected. Choose one from the top of the sidebar, or add departments under Admin → Organizations.</Alert> : null}

      <Grid container spacing={2}>
        {selectedDepartment ? (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Chip size="small" color="success" label="Manual reporting active" sx={{ mr: 1, mb: 1 }} />
                {selectedDepartment.csv_enabled_requested ? <Chip size="small" color="warning" label={`CSV: ${selectedDepartment.csv_status}`} sx={{ mr: 1, mb: 1 }} /> : null}
                {selectedDepartment.machine_enabled_requested ? <Chip size="small" color="info" label={`Machine: ${selectedDepartment.machine_status}`} sx={{ mr: 1, mb: 1 }} /> : null}
                <Typography variant="body2" color="text.secondary">Defaults: {selectedDepartment.negative_label || "Negative"} / {selectedDepartment.positive_label || "Positive"}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Create Manual Report</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth size="small" label="Patient" value={form.patient_id} onChange={(e) => setForm((s) => ({ ...s, patient_id: e.target.value }))}>
                    {patients.map((p) => <MenuItem key={p.id} value={p.id}>{valueOf(p, ["full_name", "name"]) || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.id}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth size="small" label="Provider" value={form.provider_id} onChange={(e) => setForm((s) => ({ ...s, provider_id: e.target.value }))}>
                    <MenuItem value="">—</MenuItem>
                    {providers.map((p) => <MenuItem key={p.id} value={p.id}>{valueOf(p, ["full_name", "name"]) || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.id}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth size="small" label="Facility" value={form.facility_id} onChange={(e) => setForm((s) => ({ ...s, facility_id: e.target.value }))}>
                    <MenuItem value="">—</MenuItem>
                    {facilities.map((f) => <MenuItem key={f.id} value={f.id}>{f.name || f.facility_name || f.id}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Specimen Source" value={form.specimen_source} onChange={(e) => setForm((s) => ({ ...s, specimen_source: e.target.value }))} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Sample Type" value={form.sample_type} onChange={(e) => setForm((s) => ({ ...s, sample_type: e.target.value }))} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth size="small" type="datetime-local" label="Collection Date" InputLabelProps={{ shrink: true }} value={form.collection_date} onChange={(e) => setForm((s) => ({ ...s, collection_date: e.target.value }))} /></Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              {busy ? <CircularProgress size={22} /> : analytes.length ? (
                <Grid container spacing={1}>
                  {analytes.map((a) => (
                    <Grid item xs={12} md={6} key={a.id}>
                      <FormControlLabel
                        control={<Checkbox checked={!!positive[a.id]} onChange={(e) => setPositive((s) => ({ ...s, [a.id]: e.target.checked }))} />}
                        label={`${a.display_name || a.analyte_name} (${positive[a.id] ? (a.positive_label || selectedDepartment?.positive_label || "Positive") : (a.default_result || selectedDepartment?.default_result || "Negative")})`}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="warning">This department has no analytes yet. Upload a universal analyte CSV in Admin → Organizations before creating reports.</Alert>
              )}
              <Button sx={{ mt: 2 }} variant="contained" startIcon={<AddIcon />} disabled={!departmentId || !analytes.length || creating} onClick={submitManual}>Create Final Report</Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Existing Results</Typography>
              <Box sx={{ height: 420 }}>
                <DataGrid rows={results} columns={columns} loading={busy} disableRowSelectionOnClick getRowId={(r) => r.id} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
